import i18n from "i18n";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DataTieringRepositories } from "@graylog/enterprise-api";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import { defaultOnError } from "util/conditional/onError";
import { MAX_POLL_ATTEMPTS, POLL_INTERVAL_MS } from "../../warm-tier-repositories/constants";
const urlPrefix = "/plugins/org.graylog.plugins.datatiering/datatiering/repositories";
const DATA_TIERING_REPOSITORIES_KEY = "repositories";
const DATA_TIERING_FS_PATHS_KEY = "fs_paths";
const translate = (key, options) => i18n.t(key, { ns: "enterprise_data_tiering", ...options });
const hasUnknownSignal = (repositories) => (repositories != null ? repositories : []).some(
  (repository) => repository.status === "unknown" || repository.credential_status === "unknown"
);
const createRepository = (repository) => fetch("POST", qualifyUrl(`${urlPrefix}`), repository);
const updateRepository = (repository) => fetch("PUT", qualifyUrl(`${urlPrefix}/${repository.name}`), repository);
const deleteRepository = (name) => DataTieringRepositories.remove(name);
const listRepositories = (type) => {
  const types = (Array.isArray(type) ? type : [type]).filter(Boolean);
  const query = types.map((t) => `type=${encodeURIComponent(t)}`).join("&");
  const url = query ? `${urlPrefix}?${query}` : urlPrefix;
  return fetch("GET", qualifyUrl(url));
};
const listFsPaths = () => DataTieringRepositories.allFsRepoPaths();
const useRepositories = (type, { fetchFsPaths = false, enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const [pollAttempts, setPollAttempts] = useState(0);
  const {
    data: dataRepositories,
    isLoading: isLoadingRepositories,
    dataUpdatedAt: repositoriesUpdatedAt,
    refetch: refetchRepositories
  } = useQuery({
    queryKey: [DATA_TIERING_REPOSITORIES_KEY, type],
    enabled,
    refetchInterval: (query) => hasUnknownSignal(query.state.data) && pollAttempts <= MAX_POLL_ATTEMPTS ? POLL_INTERVAL_MS : false,
    queryFn: async () => {
      const repositories = await defaultOnError(
        listRepositories(type),
        translate("notifications.repositories_loading_failed"),
        translate("notifications.repositories_could_not_load")
      );
      setPollAttempts((attempts) => hasUnknownSignal(repositories) ? attempts + 1 : 0);
      return repositories;
    }
  });
  const checksOutstanding = hasUnknownSignal(dataRepositories);
  const { data: dataFsPaths, isLoading: isLoadingFsPaths } = useQuery({
    queryKey: [DATA_TIERING_FS_PATHS_KEY],
    enabled: fetchFsPaths,
    queryFn: () => defaultOnError(
      listFsPaths(),
      translate("notifications.fs_paths_loading_failed"),
      translate("notifications.fs_paths_could_not_load")
    )
  });
  const { mutateAsync: onCreateRepository } = useMutation({
    mutationFn: createRepository,
    onSuccess: () => {
      UserNotification.success(translate("notifications.repository_created"));
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      var _a, _b, _c;
      const backendErrors = (_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors;
      if (((_c = error == null ? void 0 : error.additional) == null ? void 0 : _c.status) === 409) {
        UserNotification.error(translate("notifications.repository_request_in_progress"));
      } else if (typeof backendErrors !== "object" || backendErrors === null) {
        UserNotification.error(translate("notifications.repository_creation_failed", { error: String(error) }));
      }
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    }
  });
  const { mutateAsync: onUpdateRepository } = useMutation({
    mutationFn: updateRepository,
    onSuccess: () => {
      UserNotification.success(translate("notifications.repository_updated"));
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      UserNotification.error(translate("notifications.repository_update_failed", { error: String(error) }));
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    }
  });
  const { mutateAsync: onDeleteRepository } = useMutation({
    mutationFn: deleteRepository,
    onSuccess: () => {
      UserNotification.success(translate("notifications.repository_deleted_successfully"));
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      UserNotification.error(translate("notifications.repository_deletion_failed", { error: String(error) }));
    }
  });
  return {
    fsPaths: {
      data: dataFsPaths,
      isLoading: isLoadingFsPaths
    },
    repositories: {
      data: dataRepositories,
      isLoading: isLoadingRepositories,
      dataUpdatedAt: repositoriesUpdatedAt,
      refetch: refetchRepositories,
      checksExhausted: checksOutstanding && pollAttempts > MAX_POLL_ATTEMPTS
    },
    onCreateRepository,
    onDeleteRepository,
    onUpdateRepository
  };
};
export default useRepositories;
